import "./grid-item.scss";

import { GetCampaignModel } from "@factory/model/query-model-campaign";
import { GetRewardCampaignModel } from "@factory/model/query-model-reward";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import useScreenSize from "@shared/utility/screen-size";
import { toBoolean } from "@shared/utility/to-boolean";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Skeleton } from "primereact/skeleton";
import { classNames } from "primereact/utils";
import { useCallback } from "react";

import { appFeature } from "../../../app.feature";

export class GridItemModel {
  image: string;
  name: string;
  description: string;
  points: number;
  stamp: number;
  expirationDate: string;
  constraint: string;
  code: string;
  limitPerMonth: number;
  monthlyRedeemCount: number;
  forBirthday?: boolean;
  metadata?: object;

  constructor(
    image: string,
    name: string,
    description: string,
    points: number,
    stamp: number,
    expirationDate: string,
    constraint: string,
    code: string,
    limitPerMonth: number,
    monthlyRedeemCount: number,
    forBirthday?: boolean,
    metadata?: object,
  ) {
    this.image = image;
    this.name = name;
    this.description = description;
    this.points = points;
    this.stamp = stamp;
    this.expirationDate = expirationDate;
    this.constraint = constraint;
    this.code = code;
    this.limitPerMonth = limitPerMonth;
    this.monthlyRedeemCount = monthlyRedeemCount;
    this.forBirthday = forBirthday;
    this.metadata = metadata;
  }

  static ConvertByRewardCampaign(model: GetRewardCampaignModel) {
    return new GridItemModel(
      model?.image ?? "",
      model?.name ?? "",
      model?.description ?? "",
      model?.points ?? 0,
      model?.stamp ?? 0,
      model?.end_date ?? "",
      model?.constraint ?? "",
      model?.code ?? "",
      model?.limit_per_month ?? 0,
      model?.monthly_redeem_count ?? 0,
      model?.for_birthday ?? false,
      model,
    );
  }

  static ConvertByCampaign(model: GetCampaignModel) {
    return new GridItemModel(
      model.image || "",
      model.name || "",
      model.description || "",
      model.points || 0,
      model.stamp || 0,
      model.end_date || "",
      model.constraint || "",
      model.code || "",
      model.limit_per_month || 0,
      model.monthly_redeem_count || 0,
      model?.for_birthday ?? false,
      model,
    );
  }
}

export class CouponDataModel {
  valueType: string;
  points: number;
  stamps: number;

  constructor(valueType: string, points: number, stamps: number) {
    this.valueType = valueType;
    this.points = points;
    this.stamps = stamps;
  }
}

export interface GridItemProps {
  campaign: GridItemModel;
  shouldHideCriteria?: boolean;
  onRedeem?: (item: GridItemModel) => void;
  couponData?: CouponDataModel;
  /** When true, shows a skeleton instead of the campaign image (e.g. while the asset is loading). */
  imagePending?: boolean;
}

export function GridItem({
  campaign,
  shouldHideCriteria,
  onRedeem,
  couponData,
  imagePending,
}: GridItemProps) {
  // DECLARATIONS
  const { pointsText, stampsText } = appFeature.config.label;
  const { formatDateLocal } = useDateTimeContext();
  const { isMobileMode } = useScreenSize();

  // API DEFINITIONS
  // LOCAL FUNCTIONS
  // LOCAL RENDERS
  const redeemIcon = useCallback(() => {
    const campaignData = campaign?.metadata;
    let redeemIcon = "pi pi-gift";
    if (campaignData && "is_redeemable" in campaignData) {
      const limit_reached =
        campaign.limitPerMonth > 0 &&
        campaign?.monthlyRedeemCount >= campaign.limitPerMonth;
      redeemIcon =
        toBoolean(campaignData.is_redeemable as string) && !limit_reached
          ? "pi pi-gift"
          : "pi pi-eye";
    }

    return redeemIcon;
  }, [campaign]);

  return (
    <div id="GridItem" className="grid-item">
      <div
        className="flex border shadow-md rounded-lg overflow-hidden mb-4"
        onClick={(e) => {
          e.preventDefault();

          if (isMobileMode && onRedeem) {
            onRedeem(campaign);
          }
        }}
      >
        <div className={classNames(`flex flex-col`)}>
          {imagePending ? (
            <div className="w-48 md:w-60 flex-shrink-0">
              <Skeleton width="100%" height="12rem" />
            </div>
          ) : (
            <img
              src={campaign?.image}
              className="w-48 md:w-60 object-cover object-center"
            />
          )}
        </div>
        <div className="flex-1 flex flex-col p-4 w-full space-y-2 bg-white">
          <div>
            <div className="mb-2">
              <p className="text-lg font-bold">{campaign.name}</p>
              <p className="text-xs text-gray-600">Exclusive Member Reward</p>
            </div>
            <div
              className={classNames(
                `flex criteria w-full items-center`,
                shouldHideCriteria ? "space-x-2" : null
              )}
            >
              <div className="flex-1 bg-gray-100 rounded-md p-2">
                <p className="text-xs">{appFeature.config.label.pointsText}</p>
                {couponData !== undefined && couponData.points > 0 ? (
                  <>
                    <s>{campaign?.points}</s>
                    <span className="ml-2 text-green-500 font-semibold">
                      {couponData.valueType === "AMOUNT"
                        ? `${campaign?.points - couponData.points}`
                        : `${
                            campaign?.points -
                            campaign?.points * (couponData.points / 100)
                          }`}
                    </span>
                    <p className="text-sm text-green-500 space-x-1">
                      <span>Save</span>
                      <span>
                        {couponData.valueType === "AMOUNT"
                          ? `${couponData.points}`
                          : `${campaign?.points * (couponData.points / 100)}`}
                      </span>
                      <span>Points!</span>
                    </p>
                  </>
                ) : (
                  <>{campaign?.points}</>
                )}
              </div>
              {shouldHideCriteria ? null : (
                <div className="flex mx-2 items-center justify-center">
                  <p className="text-xs">{campaign.constraint}</p>
                </div>
              )}
              <div className="flex-1 bg-gray-100 rounded-md p-2">
                <p className="text-xs">{appFeature.config.label.stampsText}</p>
                {couponData !== undefined && couponData.stamps > 0 ? (
                  <>
                    <s>{campaign?.stamp}</s>
                    <span className="ml-2 text-green-500 font-semibold">
                      {couponData.valueType === "AMOUNT"
                        ? `${campaign?.stamp - couponData.stamps}`
                        : `${
                            campaign?.stamp -
                            campaign?.stamp * (couponData.stamps / 100)
                          }`}
                    </span>
                    <p className="text-sm text-green-500 space-x-1">
                      <span>Save</span>
                      <span>
                        {couponData.valueType === "AMOUNT"
                          ? `${couponData.stamps}`
                          : `${campaign?.stamp * (couponData.stamps / 100)}`}
                      </span>
                      <span>Stamps!</span>
                    </p>
                  </>
                ) : (
                  <>{campaign?.stamp}</>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-gray-400">
              Expires on{" "}
              <span>
                {campaign.expirationDate
                  ? formatDateLocal(campaign.expirationDate)
                  : null}
              </span>
            </p>
            {campaign.limitPerMonth > 0 && (
              <div>
                <div className="flex justify-between">
                  <p className="text-gray-400 text-xs">Monthly Limit</p>
                  <p className="text-gray-400 text-xs">
                    {campaign.monthlyRedeemCount}/{campaign.limitPerMonth} used
                  </p>
                </div>
                <ProgressBar
                  className="h-2"
                  value={
                    (campaign.monthlyRedeemCount / campaign.limitPerMonth) * 100
                  }
                />
              </div>
            )}
          </div>
          {onRedeem && !isMobileMode ? (
            <div className="flex justify-end">
              <Button
                icon={redeemIcon()}
                outlined
                onClick={() => onRedeem(campaign)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default GridItem;
